/**
 * IndividualPensionPlanModalV2
 *
 * Modal for users to register their external individual pension plans.
 * Allows connecting Indexa Capital or manual entry of plan details.
 */
import React, { useState, useEffect, useContext } from 'react';
import { Info } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import ResponsiveTooltip from '../ui/responsive-tooltip';

import { I18nContext } from '../../context/translations/TranslationsContext.context';
import saveIndexaToken from '../../api/thirdparty/indexa/saveIndexaToken';
import saveIndividualPensionPlan from '../../api/users/saveIndividualPensionPlan';
import { IndividualPensionPlan } from '../../api/users/interfaces/IndividualPensionPlan';

// Common pension plan providers
const PENSION_PROVIDERS = [
	'INDEXA CAPITAL',
	'FINIZENS',
	'MYINVESTOR',
	'CAIXABANK',
	'BBVA',
	'SANTANDER',
	'ING',
	'BANKINTER',
	'OTRO'
];

interface IndividualPensionPlanModalV2Props {
	isOpen: boolean;
	token: string;
	onSave: (data: {
		hasIndividual: boolean;
		contributionsMade: number;
		contributionsPlanned: number;
		provider: string | null;
		apiKey: string | null;
	}) => void;
	onClose?: () => void;
	/**
	 * Declaración de ESTE año, si la hay: el formulario se abre con ella cargada para
	 * poder corregirla. En blanco cuando no hay nada que editar. El backend solo
	 * devuelve la del año en curso, así que aquí nunca llega un dato caducado.
	 */
	initialPlan?: IndividualPensionPlan | null;
}

const IndividualPensionPlanModalV2: React.FC<IndividualPensionPlanModalV2Props> = ({
	isOpen,
	token,
	onSave,
	onClose,
	initialPlan
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [hasIndividual, setHasIndividual] = useState<boolean | null>(null);
	const [contributionsMade, setContributionsMade] = useState<number>(0);
	const [contributionsPlanned, setContributionsPlanned] = useState<string>('');
	const [provider, setProvider] = useState<string>('');
	const [apiKey, setApiKey] = useState<string>('');
	const [isSaving, setIsSaving] = useState(false);

	const isIndexaCapital = provider === 'INDEXA CAPITAL';

	// Una fila con `hasIndividual: false` es un "no tengo plan", no una declaración: quien
	// ahora declara de verdad está dando de alta, no editando.
	const isEditingDeclaration = !!initialPlan?.hasIndividual;

	const isFormValid = () => {
		if (hasIndividual === null) return false;
		if (hasIndividual === false) return true;
		if (hasIndividual === true) {
			// Editando solo se exige el importe. Lo demás dejaría el botón muerto sin
			// explicación a quien entra a corregir una cifra: la previsión no se persiste
			// (vuelve a salir sin marcar) y el proveedor falta en las declaraciones que
			// vienen del onboarding, que en PROD son casi todas. En un alta sí se piden.
			if (isEditingDeclaration) return true;
			if (!contributionsPlanned) return false;
			if (!provider) return false;
			return true;
		}
		return false;
	};

	const handleSave = async () => {
		setIsSaving(true);

		const pensionPlanData = {
			hasIndividual: hasIndividual!,
			contributionsMade: hasIndividual ? contributionsMade : 0,
			contributionsPlanned: 0,
			provider: hasIndividual ? provider : null
		};

		const callbackData = {
			...pensionPlanData,
			apiKey: hasIndividual && isIndexaCapital ? apiKey : null
		};

		try {
			const response = await saveIndividualPensionPlan(token, pensionPlanData);
			if (!response.success) {
				console.error('Error saving individual pension plan:', response.message);
			}

			if (hasIndividual && isIndexaCapital && apiKey.trim()) {
				try {
					await saveIndexaToken(token, { indexaApiKey: apiKey });
				} catch (error) {
					console.error('Error saving Indexa token:', error);
				}
			}

			onSave(callbackData);
		} catch (error) {
			console.error('Error saving data:', error);
			onSave(callbackData);
		} finally {
			setIsSaving(false);
		}
	};

	useEffect(() => {
		if (isOpen) {
			// Con `initialPlan` el formulario es una edición de lo declarado este año; sin
			// él, un alta en blanco. `contributionsPlanned` no se persiste en backend
			// (siempre se guarda 0), así que no hay nada que recuperar y se pregunta otra vez.
			setHasIndividual(initialPlan ? initialPlan.hasIndividual : null);
			setContributionsMade(initialPlan ? initialPlan.contributionsMade : 0);
			setContributionsPlanned('');
			setProvider(initialPlan?.provider || '');
			setApiKey('');
		}
	}, [isOpen, initialPlan]);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open && onClose) onClose();
			}}
		>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{translate('individualPensionPlan.modal.title')}
						<ResponsiveTooltip
							content={translate('individualPensionPlan.modal.tooltip')}
							side="top"
						>
							<button type="button" className="inline-flex cursor-help">
								<Info className="w-4 h-4 text-primary" />
							</button>
						</ResponsiveTooltip>
					</DialogTitle>
					<DialogDescription className="sr-only">
						{translate('individualPensionPlan.modal.srDescription')}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{/* Intro text */}
					<p className="text-sm text-gray-600">
						{translate('individualPensionPlan.modal.introText')}
					</p>

					{/* Has individual plan? */}
					<RadioGroup
						value={hasIndividual?.toString() || ''}
						onValueChange={(value) => setHasIndividual(value === 'true')}
						className="flex gap-8"
					>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="true" id="has-plan-yes" />
							<Label htmlFor="has-plan-yes">{translate('individualPensionPlan.modal.yes')}</Label>
						</div>
						<div className="flex items-center space-x-2">
							<RadioGroupItem value="false" id="has-plan-no" />
							<Label htmlFor="has-plan-no">{translate('individualPensionPlan.modal.no')}</Label>
						</div>
					</RadioGroup>

					{/* Plan details */}
					{hasIndividual === true && (
						<div className="space-y-4">
							{/* Contributions made this year */}
							<div>
								<Label htmlFor="contributions-made" className="text-sm font-medium">
									{translate('individualPensionPlan.modal.contributionsMade')}
								</Label>
								<div className="relative mt-1.5">
									<Input
										id="contributions-made"
										type="number"
										min={0}
										max={10000}
										value={contributionsMade}
										onChange={(e) => setContributionsMade(Number(e.target.value))}
										className="pr-8"
									/>
									<span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
								</div>
							</div>

							{/* Future plans */}
							<div>
								<Label className="text-sm font-medium">
									{translate('individualPensionPlan.modal.contributionsPlanned')}
								</Label>
								<RadioGroup
									value={contributionsPlanned}
									onValueChange={setContributionsPlanned}
									className="mt-2 space-y-2"
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="continue_individual" id="continue" />
										<Label htmlFor="continue" className="font-normal">
											{translate('individualPensionPlan.modal.continueIndividual')}
										</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="switch_to_company" id="switch" />
										<Label htmlFor="switch" className="font-normal">
											{translate('individualPensionPlan.modal.switchToCompany')}
										</Label>
									</div>
								</RadioGroup>
							</div>

							{/* Provider */}
							<div>
								<Label htmlFor="provider" className="text-sm font-medium">
									{translate('individualPensionPlan.modal.provider')}
								</Label>
								<select
									id="provider"
									value={provider}
									onChange={(e) => setProvider(e.target.value)}
									className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
								>
									<option value="">
										{translate('individualPensionPlan.modal.providerPlaceholder')}
									</option>
									{PENSION_PROVIDERS.map((p) => (
										<option key={p} value={p}>
											{p}
										</option>
									))}
								</select>
							</div>

							{/* Indexa Capital Integration */}
							{isIndexaCapital && (
								<div className="p-4 bg-green-50 rounded-lg border border-green-200">
									<div className="flex items-center gap-2 mb-2">
										<img
											src="/imgs/indexa-capital.png"
											alt="Indexa Capital"
											className="w-6 h-6 object-contain"
										/>
										<span className="text-sm font-medium text-primary">Indexa Capital</span>
									</div>
									<p className="text-xs text-gray-600 mb-3">
										{translate('indexaCapital.apiKeyDescription')}
									</p>
									<Input
										type="password"
										placeholder="API Key (opcional)"
										value={apiKey}
										onChange={(e) => setApiKey(e.target.value)}
									/>
									<p className="text-xs text-gray-500 mt-1">
										{translate('indexaCapital.apiKeyHelp')}
									</p>
								</div>
							)}
						</div>
					)}
				</div>

				<DialogFooter>
					<Button
						onClick={handleSave}
						disabled={!isFormValid() || isSaving}
						className="w-full bg-primary hover:bg-primary/90"
					>
						{isSaving ? translate('common.saving') : translate('individualPensionPlan.modal.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default IndividualPensionPlanModalV2;
